import { useEffect, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { lireCasquette, ecrireCasquette } from './useCasquette';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Droits effectifs de l'utilisateur courant (rôle + exceptions), chargés UNE fois
// (GET /api/permissions/me) et mémorisés en module. can('module.action') dit si
// l'action est autorisée. L'admin possède tout. Après un changement de droits
// (matrice), un rechargement de page rafraîchit le cache.
let cache = null;      // { role, set:Set<cle> }
let inflight = null;

export default function usePermissions() {
  const [snap, setSnap] = useState(cache);

  useEffect(() => {
    if (cache) { setSnap(cache); return undefined; }
    if (!inflight) {
      inflight = axios.get(`${DOMAIN}/api/permissions/me`)
        .then(({ data }) => {
          cache = {
            role: data?.role || null,
            set: new Set(data?.permissions || []),
            // Profil mixte : le serveur fait autorité (l'admin peut le révoquer
            // à tout moment) — le front ne fait qu'afficher la bascule.
            profilMixte: !!data?.profil_mixte,
          };
          return cache;
        })
        .catch(() => { cache = { role: null, set: new Set(), profilMixte: false }; return cache; })
        .finally(() => { inflight = null; });
    }
    let alive = true;
    inflight.then((c) => { if (alive) setSnap(c); });
    return () => { alive = false; };
  }, []);

  // RÉVOCATION VISIBLE (audit 25/07) : les droits étaient mémorisés jusqu'au
  // prochain rechargement complet. Après un retrait par l'admin, l'API refusait
  // bien, mais l'utilisateur restait visuellement dans l'espace vendeur. On
  // revalide donc au RETOUR AU PREMIER PLAN ; et si le profil mixte a disparu
  // alors qu'il porte la casquette vendeur, on le ramène à son hub livreur.
  useEffect(() => {
    const revalider = () => {
      if (document.visibilityState !== 'visible') return;
      axios.get(`${DOMAIN}/api/permissions/me`)
        .then(({ data }) => {
          const frais = {
            role: data?.role || null,
            set: new Set(data?.permissions || []),
            profilMixte: !!data?.profil_mixte,
          };
          const perduLeMixte = cache && cache.profilMixte && !frais.profilMixte;
          cache = frais;
          setSnap(frais);
          if (perduLeMixte && lireCasquette() === 'vendeur') ecrireCasquette('livreur');
        })
        .catch(() => { /* hors ligne : on garde les droits connus */ });
    };
    document.addEventListener('visibilitychange', revalider);
    return () => document.removeEventListener('visibilitychange', revalider);
  }, []);

  return {
    ready: !!snap,
    role: snap?.role || null,
    profilMixte: !!snap?.profilMixte,
    can: (cle) => !!(snap && snap.set && snap.set.has(cle)),
  };
}
